import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import config from '../config/config';
import { buildSbomCandidates } from '../utils/sbom';
import EmptyState from './common/EmptyState';
import Skeleton from './common/Skeleton';

const PRESETS = {
  strict: { label: 'Strict 🔴', description: 'Zero tolerance for advisories, forbidden copyleft licenses, all versions must be pinned' },
  moderate: { label: 'Moderate 🟡', description: 'Max 2 advisories per package, major copyleft licenses forbidden, no abandoned packages' },
  permissive: { label: 'Permissive 🟢', description: 'Only blocks malicious packages, minimal restrictions' },
};

const SEVERITY_COLOR = { critical: 'danger', high: 'warning', medium: 'secondary' };
const SEVERITY_ICON = { critical: '🚨', high: '⚠️', medium: 'ℹ️' };

function PolicyView({ repoName, sbomId, sbomName, onDataLoaded }) {
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const [presets, setPresets] = useState(null);
  const [rules, setRules] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const innerTab = searchParams.get('pol_tab') || 'rules';
  const selectedPreset = searchParams.get('pol_preset') || 'moderate';

  const setInnerTab = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('pol_tab', val); return n; });
  const setSelectedPreset = (val) => setSearchParams(prev => { const n = new URLSearchParams(prev); n.set('pol_preset', val); return n; });

  // Saved policies
  const [savedPolicies, setSavedPolicies] = useState([]);
  const [saveName, setSaveName] = useState('');
  const [saveDesc, setSaveDesc] = useState('');
  const [saveMsg, setSaveMsg] = useState('');
  const [savedPoliciesError, setSavedPoliciesError] = useState('');

  // History
  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState('');

  const [resolvedSbomName, setResolvedSbomName] = useState('');

  useEffect(() => {
    if (!repoName || !sbomId) return;
    let cancelled = false;
    setResolvedSbomName('');
    setError('');

    const loadPresets = async () => {
      const candidates = buildSbomCandidates(sbomId, sbomName);
      let lastError = null;

      for (const candidate of candidates) {
        try {
          const response = await axios.get(`${config.BASE_URL}/sbom/${repoName}/${candidate}/policy/presets`);
          if (cancelled) return;
          setPresets(response.data);
          setRules(prev => prev || response.data.moderate);
          setResolvedSbomName(candidate);
          if (onDataLoaded) onDataLoaded();
          return;
        } catch (err) {
          const status = err.response?.status;
          lastError = err;
          if (status !== 404) break;
        }
      }

      if (!cancelled && lastError) {
        setError(lastError.response?.data?.error || lastError.message);
      }
    };

    loadPresets();
    loadSavedPolicies();

    return () => {
      cancelled = true;
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [repoName, sbomId, sbomName]);

  const loadSavedPolicies = () => {
    setSavedPoliciesError('');
    axios.get(`${config.BASE_URL}/policies`)
      .then(r => setSavedPolicies(r.data || []))
      .catch(e => {
        setSavedPolicies([]);
        setSavedPoliciesError(e.response?.data?.error || e.message || t('policy.failedLoadSaved', 'Failed to load saved policies.'));
      });
  };

  const loadHistory = useCallback(() => {
    setHistoryLoading(true);
    setHistoryError('');
    axios.get(`${config.BASE_URL}/sbom/${repoName}/policy/history`)
      .then(r => setHistory(r.data || []))
      .catch(e => {
        setHistory([]);
        setHistoryError(e.response?.data?.error || e.message || t('policy.failedLoadHistory', 'Failed to load policy history.'));
      })
      .finally(() => setHistoryLoading(false));
  }, [repoName, t]);

  useEffect(() => {
    if (innerTab === 'history') loadHistory();
  }, [innerTab, loadHistory]);

  const handlePresetChange = (preset) => {
    setSelectedPreset(preset);
    if (presets?.[preset]) setRules({ ...presets[preset] });
    setResult(null);
    setInnerTab('rules');
  };

  const handleEvaluate = () => {
    if (!rules || !resolvedSbomName) return;
    setLoading(true);
    setError('');
    setResult(null);
    axios.post(`${config.BASE_URL}/sbom/${repoName}/${resolvedSbomName}/policy`, rules)
      .then(r => setResult(r.data))
      .catch(e => setError(e.response?.data?.error || e.message))
      .finally(() => setLoading(false));
  };

  const handleSavePolicy = () => {
    if (!saveName.trim() || !rules) return;
    axios.post(`${config.BASE_URL}/policies`, { name: saveName.trim(), description: saveDesc.trim(), rules })
      .then(() => {
        setSaveMsg(`✅ Saved as "${saveName}"`);
        setSaveName('');
        setSaveDesc('');
        loadSavedPolicies();
        setTimeout(() => setSaveMsg(''), 3000);
      })
      .catch(e => setSaveMsg(`❌ ${e.response?.data?.error || e.message}`));
  };

  const handleLoadPolicy = (p) => {
    try {
      const r = typeof p.rules === 'string' ? JSON.parse(p.rules) : p.rules;
      setRules(r);
      setSelectedPreset('');
      setResult(null);
      setInnerTab('rules');
    } catch (e) {
      setError(t('policy.failedLoadRules', 'Failed to load saved policy rules.'));
    }
  };
  const handleDeletePolicy = (id) => {
    if (!window.confirm(t('policy.deleteConfirm', 'Delete this policy?'))) return;
    axios.delete(`${config.BASE_URL}/policies/${id}`)
      .then(() => loadSavedPolicies())
      .catch(e => setSavedPoliciesError(e.response?.data?.error || e.message || t('policy.failedDeletePolicy', 'Failed to delete policy.')));
  };

  const updateRule = (key, value) => setRules(prev => ({ ...prev, [key]: value }));

  const ruleIds = result ? [...new Set(result.violations.map(v => v.rule))] : [];

  return (
    <div>
      {/* Inner tab bar */}
      <div className="ce-filter-bar" style={{ marginBottom: '1rem' }}>
        {[['rules', t('policy.tabRules', '⚙️ Rules')], ['saved', t('policy.tabSaved', '💾 Saved Policies')], ['history', t('policy.tabHistory', '📜 History')]].map(([id, label]) => (
          <button key={id} className={`ce-btn ce-btn--sm ${innerTab === id ? 'ce-btn--primary' : 'ce-btn--secondary'}`} onClick={() => setInnerTab(id)}>{label}</button>
        ))}
      </div>

      {/* ── Rules tab ── */}
      {innerTab === 'rules' && (
        <div className="row g-4">
          <div className="col-md-5">
            <div className="ce-card">
              <div className="ce-card-header">
                <h6 className="ce-card-title">{t('policy.policyRulesHeader', '📋 Policy Rules')}</h6>
              </div>
              <div className="ce-card-body">
                <div className="mb-3">
                  <label className="form-label small text-secondary fw-semibold">{t('policy.presetLabel', 'PRESET')}</label>
                  <div className="d-flex gap-2">
                    {Object.entries(PRESETS).map(([key, p]) => (
                      <button key={key} className={`ce-btn ce-btn--sm ${selectedPreset === key ? 'ce-btn--primary' : 'ce-btn--secondary'}`} onClick={() => handlePresetChange(key)}>{t(`policy.preset${key.charAt(0).toUpperCase() + key.slice(1)}`, p.label)}</button>
                    ))}
                  </div>
                  {selectedPreset && <p className="small text-secondary mt-1 mb-0">{t(`policy.preset${selectedPreset.charAt(0).toUpperCase() + selectedPreset.slice(1)}Desc`, PRESETS[selectedPreset]?.description)}</p>}
                </div>
                <hr />
                {!rules && !error && (
                  <div style={{ padding: '0.5rem 0' }}>
                    <Skeleton.Text lines={5} lastWidth="60%" />
                  </div>
                )}
                {rules && (
                  <div className="d-flex flex-column gap-3">
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="noMalicious" checked={rules.noMalicious} onChange={e => updateRule('noMalicious', e.target.checked)} />
                      <label className="form-check-label" htmlFor="noMalicious"><strong>{t('policy.ruleBlockMalicious', '☠️ Block malicious packages')}</strong><div className="small text-secondary">{t('policy.ruleBlockMaliciousDesc', 'Fail if any package is flagged as malicious in OSV')}</div></label>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMaxAdvisories', '🔒 Max advisories per package')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={-1} max={10} value={rules.maxAdvisories} onChange={e => updateRule('maxAdvisories', parseInt(e.target.value))} />
                        <span className="ce-tag" style={{ minWidth: 40 }}>{rules.maxAdvisories < 0 ? 'Off' : rules.maxAdvisories}</span>
                      </div>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMaxOutdated', '⏰ Max outdated packages')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={0} max={100} step={5} value={rules.maxOutdatedPercent} onChange={e => updateRule('maxOutdatedPercent', parseFloat(e.target.value))} />
                        <span className="ce-tag" style={{ minWidth: 40 }}>{rules.maxOutdatedPercent}%</span>
                      </div>
                    </div>
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="noAbandoned" checked={rules.noAbandoned} onChange={e => updateRule('noAbandoned', e.target.checked)} />
                      <label className="form-check-label" htmlFor="noAbandoned"><strong>{t('policy.ruleBlockAbandoned', '👻 Block abandoned packages')}</strong><div className="small text-secondary">{t('policy.ruleBlockAbandonedDesc', 'Fail if maintenance score below 3')}</div></label>
                    </div>
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="requirePinned" checked={rules.requirePinned} onChange={e => updateRule('requirePinned', e.target.checked)} />
                      <label className="form-check-label" htmlFor="requirePinned"><strong>{t('policy.ruleRequirePinned', '📌 Require pinned versions')}</strong><div className="small text-secondary">{t('policy.ruleRequirePinnedDesc', 'Fail on floating version ranges like ^1.2.0')}</div></label>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMinScorecard', '🏆 Min OpenSSF Scorecard score')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={-1} max={10} step={0.5} value={rules.minScorecardScore} onChange={e => updateRule('minScorecardScore', parseFloat(e.target.value))} />
                        <span className="ce-tag" style={{ minWidth: 40 }}>{rules.minScorecardScore < 0 ? 'Off' : rules.minScorecardScore}</span>
                      </div>
                    </div>
                    <hr className="my-1" />
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMinCraScore', '🇪🇺 Min CRA score')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={-1} max={100} step={5} value={rules.minCraScore ?? -1} onChange={e => updateRule('minCraScore', parseInt(e.target.value, 10))} />
                        <span className="ce-tag" style={{ minWidth: 48 }}>{(rules.minCraScore ?? -1) < 0 ? 'Off' : rules.minCraScore}</span>
                      </div>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMinCraGrade', '🇪🇺 Min CRA grade')}</label>
                      <select className="form-select form-select-sm" value={rules.minCraGrade || ''} onChange={e => updateRule('minCraGrade', e.target.value)}>
                        <option value="">Off</option>
                        {['A', 'B', 'C', 'D'].map(g => <option key={g} value={g}>{g}</option>)}
                      </select>
                    </div>
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="requireCraTimestamp" checked={!!rules.requireCraTimestamp} onChange={e => updateRule('requireCraTimestamp', e.target.checked)} />
                      <label className="form-check-label" htmlFor="requireCraTimestamp"><strong>{t('policy.ruleRequireCraTimestamp', '🕒 Require CRA timestamp')}</strong><div className="small text-secondary">{t('policy.ruleRequireCraTimestampDesc', 'Fail if the SBOM has no creation timestamp')}</div></label>
                    </div>
                    <div className="form-check form-switch">
                      <input className="form-check-input" type="checkbox" id="requireCraDependencies" checked={!!rules.requireCraDependencies} onChange={e => updateRule('requireCraDependencies', e.target.checked)} />
                      <label className="form-check-label" htmlFor="requireCraDependencies"><strong>{t('policy.ruleRequireCraDeps', '🕸️ Require dependency relationships')}</strong><div className="small text-secondary">{t('policy.ruleRequireCraDepsDesc', 'Fail if the SBOM has no dependency graph relationships')}</div></label>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMaxMissingPurl', '🔗 Max missing PURL %')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={-1} max={100} step={5}
                          value={rules.maxMissingPurlPercent ?? -1}
                          onChange={e => updateRule('maxMissingPurlPercent', parseFloat(e.target.value))} />
                        <span className="ce-tag" style={{ minWidth: 48 }}>
                          {(rules.maxMissingPurlPercent ?? -1) < 0 ? 'Off' : `${rules.maxMissingPurlPercent}%`}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMaxMissingCpe', '🧷 Max missing CPE %')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={-1} max={100} step={5}
                          value={rules.maxMissingCpePercent ?? -1}
                          onChange={e => updateRule('maxMissingCpePercent', parseFloat(e.target.value))} />
                        <span className="ce-tag" style={{ minWidth: 48 }}>
                          {(rules.maxMissingCpePercent ?? -1) < 0 ? 'Off' : `${rules.maxMissingCpePercent}%`}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMaxMissingBothIds', '🪪 Max missing both identifiers %')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={-1} max={100} step={5}
                          value={rules.maxMissingIdentifiersPercent ?? -1}
                          onChange={e => updateRule('maxMissingIdentifiersPercent', parseFloat(e.target.value))} />
                        <span className="ce-tag" style={{ minWidth: 48 }}>
                          {(rules.maxMissingIdentifiersPercent ?? -1) < 0 ? 'Off' : `${rules.maxMissingIdentifiersPercent}%`}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMaxMissingSupplier', '🏢 Max missing supplier %')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={-1} max={100} step={5}
                          value={rules.maxMissingSupplierPercent ?? -1}
                          onChange={e => updateRule('maxMissingSupplierPercent', parseFloat(e.target.value))} />
                        <span className="ce-tag" style={{ minWidth: 48 }}>
                          {(rules.maxMissingSupplierPercent ?? -1) < 0 ? 'Off' : `${rules.maxMissingSupplierPercent}%`}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMaxMissingHash', '#️⃣ Max missing hash %')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={-1} max={100} step={5}
                          value={rules.maxMissingHashesPercent ?? -1}
                          onChange={e => updateRule('maxMissingHashesPercent', parseFloat(e.target.value))} />
                        <span className="ce-tag" style={{ minWidth: 48 }}>
                          {(rules.maxMissingHashesPercent ?? -1) < 0 ? 'Off' : `${rules.maxMissingHashesPercent}%`}
                        </span>
                      </div>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleForbiddenLicenses', '⚖️ Forbidden licenses')}</label>
                      <input type="text" className="form-control form-control-sm" value={(rules.forbiddenLicenses || []).join(', ')} onChange={e => updateRule('forbiddenLicenses', e.target.value.split(',').map(l => l.trim()).filter(Boolean))} placeholder={t('policy.ruleForbiddenLicensePlaceholder', 'e.g. GPL-3.0, AGPL-3.0')} />
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleForbiddenCategories', '🔴 Forbidden license categories')}</label>
                      <div className="d-flex flex-wrap gap-2">
                        {['STRONG_COPYLEFT', 'WEAK_COPYLEFT', 'UNKNOWN'].map(cat => {
                          const checked = (rules.forbiddenLicenseCategories || []).includes(cat);
                          const labels = { STRONG_COPYLEFT: t('policy.catStrongCopyleft', '🔴 Strong Copyleft (GPL/AGPL)'), WEAK_COPYLEFT: t('policy.catWeakCopyleft', '🟡 Weak Copyleft (LGPL/MPL)'), UNKNOWN: t('policy.catUnknownLicense', '⚪ Unknown license') };
                          return (
                            <div key={cat} className="form-check form-check-inline">
                              <input className="form-check-input" type="checkbox" id={`cat-${cat}`} checked={checked}
                                onChange={e => {
                                  const cats = rules.forbiddenLicenseCategories || [];
                                  updateRule('forbiddenLicenseCategories', e.target.checked ? [...cats, cat] : cats.filter(c => c !== cat));
                                }} />
                              <label className="form-check-label small" htmlFor={`cat-${cat}`}>{labels[cat]}</label>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                    <div>
                      <label className="form-label small fw-semibold">{t('policy.ruleMaxUnknownLicense', '⚪ Max unknown license %')}</label>
                      <div className="d-flex align-items-center gap-2">
                        <input type="range" className="form-range flex-fill" min={-1} max={100} step={5}
                          value={rules.maxUnknownLicensePercent ?? -1}
                          onChange={e => updateRule('maxUnknownLicensePercent', parseFloat(e.target.value))} />
                        <span className="ce-tag" style={{ minWidth: 40 }}>
                          {(rules.maxUnknownLicensePercent ?? -1) < 0 ? 'Off' : `${rules.maxUnknownLicensePercent}%`}
                        </span>
                      </div>
                    </div>
                  </div>
                )}
                <hr />
                {/* Save current rules */}
                <div>
                  <label className="form-label small fw-semibold text-secondary">{t('policy.saveAsPolicy', '💾 Save as named policy')}</label>
                  <input type="text" className="form-control form-control-sm mb-1" placeholder={t('policy.policyNamePlaceholder', 'Policy name')} value={saveName} onChange={e => setSaveName(e.target.value)} />
                  <input type="text" className="form-control form-control-sm mb-2" placeholder={t('policy.descriptionPlaceholder', 'Description (optional)')} value={saveDesc} onChange={e => setSaveDesc(e.target.value)} />
                  <button className="ce-btn ce-btn--secondary ce-btn--sm" style={{ width: '100%' }} onClick={handleSavePolicy} disabled={!saveName.trim()}>{t('policy.savePolicy', '💾 Save Policy')}</button>
                  {saveMsg && <div className="small mt-1 text-center">{saveMsg}</div>}
                </div>
                <div className="mt-3">
                  <button className="ce-btn ce-btn--primary" style={{ width: '100%' }} onClick={handleEvaluate} disabled={loading || !rules}>
                    {loading ? <><span className="spinner-border spinner-border-sm me-2" />{t('policy.evaluating', 'Evaluating…')}</> : t('policy.runPolicyCheck', '▶ Run Policy Check')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Results */}
          <div className="col-md-7">
            {error && <div className="alert alert-danger">{error}</div>}
            {!result && !loading && !error && (
              <EmptyState
                icon="📋"
                title={t('policy.emptyRulesTitle')}
                description={t('policy.emptyRulesDescription')}
                size="sm"
              />
            )}
            {result && (
              <div>
                <div className="ce-card" style={{ marginBottom: '1rem', borderLeft: `3px solid ${result.passed ? '#16a34a' : '#dc2626'}` }}>
                  <div className="ce-card-body" style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                    <span style={{ fontSize: '2rem' }}>{result.passed ? '✅' : '❌'}</span>
                    <div style={{ flex: 1 }}><strong style={{ display: 'block' }}>{result.passed ? t('policy.policyPassed', 'Policy PASSED') : t('policy.policyFailed', 'Policy FAILED')}</strong><span>{result.summary}</span></div>
                    <div style={{ textAlign: 'right' }}><div style={{ fontSize: '1.5rem', fontWeight: 700 }}>{result.score}</div><div style={{ fontSize: '0.75rem', color: 'var(--bs-secondary-color)' }}>/ 100</div></div>
                  </div>
                </div>
                {result.violations.length > 0 && (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
                    {ruleIds.map(rule => {
                      const viols = result.violations.filter(v => v.rule === rule);
                      const severity = viols[0].severity;
                      const sevCls = severity === 'critical' ? 'critical' : severity === 'high' ? 'high' : 'medium';
                      return (
                        <div key={rule} className="ce-card" style={{ borderLeft: `3px solid ${severity === 'critical' ? '#dc2626' : severity === 'high' ? '#f59e0b' : '#6b7280'}` }}>
                          <div className="ce-card-header">
                            <span>{SEVERITY_ICON[severity]}</span>
                            <strong style={{ flex: 1, textTransform: 'capitalize' }}>{rule.replace(/-/g, ' ')}</strong>
                            <span className={`ce-sev ce-sev--${sevCls}`}>{viols.length} {viols.length > 1 ? t('policy.violations', 'violations') : t('policy.violation', 'violation')}</span>
                          </div>
                          <ul style={{ listStyle: 'none', padding: 0, margin: 0, maxHeight: 200, overflowY: 'auto' }}>
                            {viols.map((v, i) => (
                              <li key={i} style={{ padding: '0.5rem 1rem', borderTop: '1px solid var(--bs-border-color)', display: 'flex', alignItems: 'flex-start', gap: '0.5rem' }}>
                                {v.package && <code style={{ fontSize: '0.8125rem', whiteSpace: 'nowrap' }}>{v.package}</code>}
                                <span style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)' }}>{v.description}</span>
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Saved Policies tab ── */}
      {innerTab === 'saved' && (
        <div>
          {savedPoliciesError && <div className="alert alert-danger">{savedPoliciesError}</div>}
          {savedPolicies.length === 0 ? (
            <EmptyState
              icon="💾"
              title={t('policy.emptySavedTitle')}
              description={t('policy.emptySavedDescription')}
              size="sm"
            />
          ) : (
            <div className="d-flex flex-column gap-3">
              {savedPolicies.map(p => {
                const r = typeof p.rules === 'string' ? JSON.parse(p.rules) : p.rules;
                return (
                  <div key={p.id} className="ce-card">
                    <div className="ce-card-body" style={{ display: 'flex', alignItems: 'flex-start', gap: '1rem' }}>
                      <div className="flex-fill">
                        <strong>{p.name}</strong>
                        {p.description && <div className="small text-secondary">{p.description}</div>}
                        <div className="mt-1 d-flex flex-wrap gap-1">
                          {r.noMalicious && <span className="badge bg-danger bg-opacity-10 text-danger border border-danger-subtle">☠️ no malicious</span>}
                          {r.noAbandoned && <span className="badge bg-warning bg-opacity-10 text-warning border border-warning-subtle">👻 no abandoned</span>}
                          {r.requirePinned && <span className="badge bg-secondary bg-opacity-10 text-secondary border">📌 pinned</span>}
                          {r.maxAdvisories >= 0 && <span className="badge bg-secondary bg-opacity-10 text-secondary border">max {r.maxAdvisories} CVEs</span>}
                          {(r.minCraScore ?? -1) >= 0 && <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">🇪🇺 min CRA {r.minCraScore}</span>}
                          {r.minCraGrade && <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">🇪🇺 grade ≥ {r.minCraGrade}</span>}
                          {r.requireCraTimestamp && <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">🕒 CRA timestamp</span>}
                          {r.requireCraDependencies && <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">🕸️ CRA dependencies</span>}
                          {(r.maxMissingPurlPercent ?? -1) >= 0 && <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">🔗 max missing PURL {r.maxMissingPurlPercent}%</span>}
                          {(r.maxMissingCpePercent ?? -1) >= 0 && <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">🧷 max missing CPE {r.maxMissingCpePercent}%</span>}
                          {(r.maxMissingIdentifiersPercent ?? -1) >= 0 && <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">🪪 max missing both IDs {r.maxMissingIdentifiersPercent}%</span>}
                          {(r.maxMissingSupplierPercent ?? -1) >= 0 && <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">🏢 max missing supplier {r.maxMissingSupplierPercent}%</span>}
                          {(r.maxMissingHashesPercent ?? -1) >= 0 && <span className="badge bg-primary bg-opacity-10 text-primary border border-primary-subtle">#️⃣ max missing hash {r.maxMissingHashesPercent}%</span>}
                          {(r.forbiddenLicenses || []).length > 0 && <span className="badge bg-secondary bg-opacity-10 text-secondary border">⚖️ {r.forbiddenLicenses.length} forbidden licenses</span>}
                          {(r.forbiddenLicenseCategories || []).length > 0 && <span className="badge bg-secondary bg-opacity-10 text-secondary border">🔴 {r.forbiddenLicenseCategories.join(', ')}</span>}
                        </div>
                        <div className="small text-secondary mt-1">{t('policy.created', 'Created')} {p.createdAt?.substring(0, 10)}</div>
                      </div>
                      <div className="d-flex gap-2">
                        <button className="ce-btn ce-btn--secondary ce-btn--sm" onClick={() => handleLoadPolicy(p)}>{t('policy.loadButton', 'Load')}</button>
                        <button className="ce-btn ce-btn--danger ce-btn--sm" onClick={() => handleDeletePolicy(p.id)}>{t('policy.deleteButton', 'Delete')}</button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}

      {/* ── History tab ── */}
      {innerTab === 'history' && (
        <div>
          {historyError && <div className="alert alert-danger mb-3">{historyError}</div>}
          {historyLoading ? (
            <div style={{ padding: '0.5rem 0' }}>
              <Skeleton.Table rows={4} cols={6} />
            </div>
          ) : history.length === 0 ? (
            <EmptyState
              icon="📜"
              title={t('policy.emptyHistoryTitle')}
              description={t('policy.emptyHistoryDescription')}
              size="sm"
            />
          ) : (
            <div className="ce-table-wrap">
              <table className="ce-table">
                <thead>
                  <tr>
                    <th>{t('policy.columns.date')}</th>
                    <th>{t('policy.columns.policy')}</th>
                    <th>{t('policy.columns.commit')}</th>
                    <th>{t('policy.columns.result')}</th>
                    <th>{t('policy.columns.score')}</th>
                    <th>{t('policy.columns.violations')}</th>
                  </tr>
                </thead>
                <tbody>
                  {history.map(h => (
                    <tr key={h.id}>
                      <td style={{ fontSize: '0.8125rem', color: 'var(--bs-secondary-color)', whiteSpace: 'nowrap' }}>{h.ranAt?.substring(0, 16).replace('T', ' ')}</td>
                      <td><span className="ce-tag">{h.policyName || 'ad-hoc'}</span></td>
                      <td><code className="ce-mono">{h.commitHash?.substring(0, 10)}</code></td>
                      <td>{h.passed ? <span className="ce-sev ce-sev--low">✓ Pass</span> : <span className="ce-sev ce-sev--critical">✗ Fail</span>}</td>
                      <td><strong>{h.score}</strong><span style={{ color: 'var(--bs-secondary-color)', fontSize: '0.75rem' }}>/100</span></td>
                      <td>{h.violations > 0 ? <span className="ce-sev ce-sev--medium">{h.violations}</span> : <span style={{ color: 'var(--bs-success-color, #16a34a)' }}>0</span>}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default PolicyView;
